import { useState, useEffect } from "react";
import { serverNow } from "../../../shared/serverClock";
import { formatTime, formatTimeLabeled } from "../../../shared/utils";
import { useIsClockSynced } from "../context/ServerClockContext";

export interface UseExamTimerReturn {
  timerDisplay: string;
  remainingSeconds: number;
  isExpired: boolean;
  showUnits?: boolean;
}

export function useExamTimer(
  totalDurationSeconds: number,
  examStartTime: string | null,
  paused?: boolean,
  showUnits?: boolean,
): UseExamTimerReturn {
  const isSynced = useIsClockSynced();
  const [remainingSeconds, setRemainingSeconds] =
    useState(totalDurationSeconds);

  useEffect(() => {
    if (!isSynced || paused) return;

    const compute = () => {
      if (!examStartTime) return totalDurationSeconds;
      const startMs = new Date(examStartTime).getTime();
      if (isNaN(startMs)) return totalDurationSeconds;
      const elapsed = (serverNow() - startMs) / 1000;
      return Math.max(0, Math.floor(totalDurationSeconds - elapsed));
    };

    setRemainingSeconds(compute());
    const interval = setInterval(() => setRemainingSeconds(compute()), 1000);
    return () => clearInterval(interval);
  }, [isSynced, totalDurationSeconds, examStartTime, paused]);

  return {
    timerDisplay: showUnits
      ? formatTimeLabeled(remainingSeconds)
      : formatTime(remainingSeconds),
    remainingSeconds,
    isExpired: remainingSeconds <= 0,
  };
}
